import { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import Modal from '@mui/material/Modal';
import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import CloseIcon from '@mui/icons-material/Close';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import { FormControlLabel, FormGroup } from '@mui/material';
import { cloneDeep } from 'lodash';

import UploadFile from 'app/components/UploadFile';
import classes from './classes.module.scss';

import {
  localeOptions
} from 'app/utils/options';

import {
  updateKioskLocale,
  uploadKioskLocaleAsset,
  getKioskLocaleAssetUrl
} from 'app/actions/kiosks';

import {
  KIOSK_BACKGROUND_URL,
  KIOSK_LOGO_URL
} from 'app/utils/Constants';

const KioskLocaleForm = props => {
  const {
    kiosk,
    kioskLocales,
    isOpen,
    onClose,
    onSubmit,
    updateKioskLocale,
    uploadKioskLocaleAsset
  } = props;

  const [localeMap, setLocaleMap] =  useState([]);
  const [tabIndex, setTabIndex] = useState(0);

  // build localeMap on first render
  useEffect(() => {
    setLocaleMap(kioskLocales.reduce((map, locale) => {
      map[locale.locale] = locale;
      return map
    }, {}));
  }, []);

  const onTabChange = (event, newValue) => {
    setTabIndex(newValue);
  }

  const onKioskLocaleChange = (locale, field, value, type) => {
    if (type === 'integer') {
      value = parseInt(value);
    }

    let map = cloneDeep(localeMap);
    map[locale][field] = value;

    setLocaleMap(map);
  }

  const onBackgroundChange = async (event, locale) => {
    if (event.target.files && event.target.files[0]) {
      let file = event.target.files[0];

      if (file) {
        await uploadKioskLocaleAsset(locale.kiosk_id, locale.id, 'background', file);

        let fileUrl = URL.createObjectURL(file);
        locale.background_url = fileUrl;
        locale.background_mime_type = file.type;

        onKioskLocaleChange(locale.locale, 'background_url', fileUrl);
        onKioskLocaleChange(locale.locale, 'background_mime_type', file.type);
      }
    }
  }

  const onLogoChange = async (event, locale) => {
    if (event.target.files && event.target.files[0]) {
      let file = event.target.files[0];

      if (file) {
        await uploadKioskLocaleAsset(locale.kiosk_id, locale.id, 'logo', file);

        let fileUrl = URL.createObjectURL(file);
        locale.logo_url = fileUrl;
        locale.logo_mime_type = file.type;

        onKioskLocaleChange(locale.locale, 'logo_url', fileUrl);
        onKioskLocaleChange(locale.locale, 'logo_mime_type', file.type);
      }
    }
  }

  const onFormSubmit = () => {
    let locales = Object.keys(localeMap);

    for (let locale of locales) {
      let kioskLocale = localeMap[locale];
      updateKioskLocale(kioskLocale.kiosk_id, kioskLocale.id, kioskLocale);
    }

    onSubmit(localeMap);
  }

  let kioskLocaleOptions = localeOptions;

  let buttons = [
    // cancel / reset
    {
      variant: 'outlined',
      size: 'large',
      label: 'cancel',
      onClick: onClose
    },

    // submit / save
    {
      variant: 'contained',
      size: 'large',
      label: 'save',
      onClick: onFormSubmit
    }
  ]

  const localeTabs = kiosk.locales
    .map(locale => {
      const selectedLocale = kioskLocaleOptions.find(x => x.value === locale.locale);
      return selectedLocale ? { locale, selectedLocale } : null;
    })
    .filter(Boolean);

  const activeTabIndex = tabIndex < localeTabs.length ? tabIndex : 0;
  const activeTab = localeTabs[activeTabIndex];

  let localePanel = null;
  if (activeTab) {
    const { locale, selectedLocale } = activeTab;
    const currentLocale = localeMap[selectedLocale.value] || {};
    const bgImgUrl = KIOSK_BACKGROUND_URL(kiosk.token, selectedLocale.value);
    const logoImgUrl = KIOSK_LOGO_URL(kiosk.token, selectedLocale.value);
    const panelId = `kiosk-locale-panel-${selectedLocale.value}`;
    const tabId = `kiosk-locale-tab-${selectedLocale.value}`;

    localePanel = (
      <div
        className={classes.tabPanel}
        role='tabpanel'
        id={panelId}
        aria-labelledby={tabId}
      >
        <FormGroup className={classes.formFields}>
          <TextField
            className={classes.field}
            fullWidth
            label='Header'
            value={currentLocale.header || ''}
            onChange={e => onKioskLocaleChange(locale.locale, 'header', e.target.value)}
          />
          <TextField
            className={classes.field}
            fullWidth
            multiline
            minRows={2}
            maxRows={6}
            label='Login Message'
            value={currentLocale.login_message || ''}
            onChange={e => onKioskLocaleChange(locale.locale, 'login_message', e.target.value)}
          />
          <TextField
            className={classes.field}
            fullWidth
            multiline
            minRows={2}
            maxRows={6}
            label='Opt In Message'
            value={currentLocale.opt_in_message || ''}
            onChange={e => onKioskLocaleChange(locale.locale, 'opt_in_message', e.target.value)}
          />
          <hr className={classes.divider} />
          <div className={classes.assetSection}>
            <Typography variant='h6' className={classes.sectionTitle}>Background</Typography>
            <img src={bgImgUrl} alt='' className={classes.imgComponent} />
            <UploadFile onFileChange={e => onBackgroundChange(e, locale)} />
          </div>
          <hr className={classes.divider} />
          <div className={classes.assetSection}>
            <Typography variant='h6' className={classes.sectionTitle}>Logo</Typography>
            <img src={logoImgUrl} alt='' className={classes.imgComponent} />
            <UploadFile onFileChange={e => onLogoChange(e, locale)} />
          </div>
          <FormControlLabel
            control={
              <Checkbox
                checked={Boolean(currentLocale.enabled)}
                onChange={e => onKioskLocaleChange(locale.locale, 'enabled', e.target.checked)}
              />
            }
            label='Enabled'
          />
          <Box className={classes.buttons} display='flex'>
            {buttons.map(button => (
              <Button
                key={'button-' + button.label}
                className={classes.button}
                variant={button.variant}
                size={button.size}
                onClick={button.onClick}>
                {button.label}
              </Button>
            ))}
          </Box>
        </FormGroup>
      </div>
    );
  }

  return (
    <div>
      <Modal open={isOpen}
        onClose={onClose}
        className={classes.modal}>
        <Box className={classes.paper}>
          <CloseIcon className={classes.closeIcon} onClick={onClose} />
          <Typography variant='h5' className={classes.formTitle}>
            Manage Locales
          </Typography>
          <hr className={classes.divider} />
          <Tabs
            className={classes.tabs}
            value={localeTabs.length ? activeTabIndex : false}
            onChange={onTabChange}
            variant='scrollable'
            scrollButtons='auto'
            allowScrollButtonsMobile
            aria-label='Kiosk locale settings'
          >
            {localeTabs.map(({ locale, selectedLocale }) => (
              <Tab
                key={locale.locale}
                label={selectedLocale.label}
                id={`kiosk-locale-tab-${selectedLocale.value}`}
                aria-controls={`kiosk-locale-panel-${selectedLocale.value}`}
              />
            ))}
          </Tabs>
          {localePanel}
        </Box>
      </Modal>
    </div>
  )
}

const mapStateToProps = state => ({
});

export default connect(mapStateToProps, { updateKioskLocale, uploadKioskLocaleAsset, getKioskLocaleAssetUrl })(KioskLocaleForm);
